/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BasicResponseChannelsResponse } from '../models/BasicResponseChannelsResponse';
import type { BasicResponseConnectionStatusResponse } from '../models/BasicResponseConnectionStatusResponse';
import type { BasicResponseMappingResponse } from '../models/BasicResponseMappingResponse';
import type { BasicResponseMappingsResponse } from '../models/BasicResponseMappingsResponse';
import type { BasicResponseOAuthStartResponse } from '../models/BasicResponseOAuthStartResponse';
import type { BasicResponseTestMessageResponse } from '../models/BasicResponseTestMessageResponse';
import type { BasicResponseUsersResponse } from '../models/BasicResponseUsersResponse';
import type { BasicResponseVoid } from '../models/BasicResponseVoid';
import type { MappingRequest } from '../models/MappingRequest';
import type { RedirectView } from '../models/RedirectView';
import type { TestMessageRequest } from '../models/TestMessageRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SlackApiService {

    /**
     * Slack OAuth 콜백 처리
     * Slack OAuth 인증 완료 후 콜백을 처리하고 프론트엔드로 리디렉션합니다.
     * @returns RedirectView OK
     * @throws ApiError
     */
    public static handleCallback({
        code,
        state,
        error,
    }: {
        code: string,
        state?: string,
        error?: string,
    }): CancelablePromise<RedirectView> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/oauth/callback',
            query: {
                'code': code,
                'state': state,
                'error': error,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack OAuth 콜백 처리
     * Slack OAuth 인증 완료 후 콜백을 처리하고 프론트엔드로 리디렉션합니다.
     * @returns RedirectView OK
     * @throws ApiError
     */
    public static handleCallback1({
        code,
        state,
        error,
    }: {
        code: string,
        state?: string,
        error?: string,
    }): CancelablePromise<RedirectView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/binder-app/integrations/slack/oauth/callback',
            query: {
                'code': code,
                'state': state,
                'error': error,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 테스트 메시지 전송
     * 설정된 알림 매핑을 테스트하기 위해 샘플 메시지를 전송합니다
     * @returns BasicResponseTestMessageResponse OK
     * @throws ApiError
     */
    public static sendTestMessage({
        requestBody,
    }: {
        requestBody: TestMessageRequest,
    }): CancelablePromise<BasicResponseTestMessageResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/binder-app/integrations/slack/test-message',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 알림 매핑 목록 조회
     * 현재 사용자의 모든 알림 매핑 설정을 조회합니다
     * @returns BasicResponseMappingsResponse OK
     * @throws ApiError
     */
    public static getMappings(): CancelablePromise<BasicResponseMappingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/mappings',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 알림 매핑 생성/수정
     * 알림 유형별로 Slack 채널 또는 사용자에게 전송할 매핑을 설정합니다
     * @returns BasicResponseMappingResponse OK
     * @throws ApiError
     */
    public static createOrUpdateMapping({
        requestBody,
    }: {
        requestBody: MappingRequest,
    }): CancelablePromise<BasicResponseMappingResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/binder-app/integrations/slack/mappings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack 사용자 목록 조회
     * 연결된 Slack 워크스페이스의 사용자 목록을 조회합니다
     * @returns BasicResponseUsersResponse OK
     * @throws ApiError
     */
    public static getUsers(): CancelablePromise<BasicResponseUsersResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/users',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack OAuth 인증 시작
     * Slack 워크스페이스 연동을 위한 OAuth 인증을 시작합니다
     * @returns BasicResponseOAuthStartResponse OK
     * @throws ApiError
     */
    public static startOAuth(): CancelablePromise<BasicResponseOAuthStartResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/oauth/start',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack 연결 상태 조회
     * 현재 사용자의 Slack 워크스페이스 연결 상태를 조회합니다
     * @returns BasicResponseConnectionStatusResponse OK
     * @throws ApiError
     */
    public static getConnectionStatus(): CancelablePromise<BasicResponseConnectionStatusResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/connection/status',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack 채널 목록 조회
     * 연결된 Slack 워크스페이스의 채널 목록을 조회합니다
     * @returns BasicResponseChannelsResponse OK
     * @throws ApiError
     */
    public static getChannels(): CancelablePromise<BasicResponseChannelsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/binder-app/integrations/slack/channels',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * 알림 매핑 삭제
     * 지정된 알림 매핑 설정을 삭제합니다
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static deleteMapping({
        mappingId,
    }: {
        mappingId: string,
    }): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/binder-app/integrations/slack/mappings/{mappingId}',
            path: {
                'mappingId': mappingId,
            },
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

    /**
     * Slack 연결 해제
     * 현재 사용자의 Slack 워크스페이스 연결을 해제합니다
     * @returns BasicResponseVoid OK
     * @throws ApiError
     */
    public static disconnectSlack(): CancelablePromise<BasicResponseVoid> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/binder-app/integrations/slack/connection',
            errors: {
                400: `Bad Request`,
                403: `Forbidden`,
                404: `Not Found`,
                409: `Conflict`,
                500: `Internal Server Error`,
            },
        });
    }

}
